import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import classes from './ViewPatient.module.scss';
import { FullPageContainer } from '../../common/FullPageContainer/FullPageContainer';
import { ViewPatientControls } from './components/ViewPatientControls/ViewPatientControls';
import {
  APPLY_TREATMENT_PLAN_REQUEST,
  DELETE_PATIENT_REQUEST,
  GET_ALARMS_SUCCESS,
  GET_PATIENT_REQUEST,
  GET_PATIENT_HISTORY_REQUEST,
  SET_CURRENT_PATIENT,
} from '../../../redux/actions';
import { currentPatientSelector } from '../../../redux/selectors/patientsSelector';
import { ViewPatientInfo } from './components/ViewPatientInfo/ViewPatientInfo';
import { ClickingOverview } from './components/ClickingOverview/ClickingOverview';
import { ApplyTreatmentPlan } from '../../common/ApplyTreatmentPlan/ApplyTreatmentPlan';
import { format } from 'date-fns';
import { Modal } from '../../common/Modal/Modal';
import { Alert, Drawer, Tab, Tabs as MUITabs } from '@mui/material';
import { Alarms } from './components/Alarms/Alarms';
import { a11yProps, operationTypeToBone } from '../../../utils/commonUtils';
import { AssignExercises } from './components/AssignExercises/AssignExercises';
import { AssignExercisesModal } from './components/AssignExercisesModal/AssignExercisesModal';
import { ExerciseGroup } from '../../../types/exercises';
import { ExercisePerformanceChart } from './components/ExercisePerformanceChart/ExercisePerformanceChart';
import {Medications} from './components/Medications/Medications';
import {StepsAsymmetry} from './components/StepsAsymmetry/StepsAsymmetry';
import {Steps} from './components/Steps/Steps';
import {AssignMedicationTemplate} from './components/AssignMedicationTemplate/AssignMedicationTemplate';
import { PatientHistorySelector } from './components/PatientHistorySelector/PatientHistorySelector';
import {
  patientHistoryLoadingSelector,
  patientHistorySelector,
} from '../../../redux/selectors/patientsSelector';
import { ARCHIVED_STATUS } from '../../../utils/constants';

enum Tabs {
  CLICKING_PROGRESS_OVERVIEW,
  EXERCISE_PERFORMANCE,
  MEDICATIONS,
  STEPS,
  STEP_ASYMMETRY,
}

const tabs = [
  {
    id: Tabs.CLICKING_PROGRESS_OVERVIEW,
    label: 'Clicking Progress Overview',
  },
  {
    id: Tabs.EXERCISE_PERFORMANCE,
    label: 'Exercise Performance',
  },
  {
    id: Tabs.STEPS,
    label: 'Steps',
  },
  {
    id: Tabs.STEP_ASYMMETRY,
    label: 'Steps asymmetry',
  },
  {
    id: Tabs.MEDICATIONS,
    label: 'Medications',
  },
];

export const ViewPatient = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const id = location.pathname.split('/').pop() || '';
  const [applyTreatPlan, setApplyTreatPlan] = useState<boolean>(false);
  const [selectedPlan, setSelectedPlan] = useState<any>(null);
  const [startDate, setStartDate] = useState<any>(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isOpenAlarms, setIsOpenAlarms] = useState(false);
  const [isAssignExercisesModalOpened, setAssignExercisesModalOpened] = useState(false);
  const [assignExercisesModalProps, setAssignExercisesModalProps] = useState<ExerciseGroup>();
  const [selectedPatientId, setSelectedPatientId] = useState(id);

  const [currentTab, setCurrentTab] = useState(Tabs.CLICKING_PROGRESS_OVERVIEW);

  const openAssignExerciseModal = (group: ExerciseGroup) => {
    if (isReadOnly) return;

    setAssignExercisesModalOpened(true);
    setAssignExercisesModalProps(group);
  };
  const closeAssignExerciseModal = () => setAssignExercisesModalOpened(false);

  const toggleDrawer = () => setIsOpenAlarms((prev) => !prev);

  const shouldShowAlarms = location.search.includes('open-alarms');

  const currentPatient = useSelector(currentPatientSelector(selectedPatientId));
  const routePatient = useSelector(currentPatientSelector(id));
  const patientHistory = useSelector(patientHistorySelector(id));
  const isPatientHistoryLoading = useSelector(patientHistoryLoadingSelector);
  const isHistoricalVersion = selectedPatientId !== id;
  const isReadOnly = isHistoricalVersion || currentPatient?.status === ARCHIVED_STATUS;

  // clear current patient on unmount
  useEffect(
    () => () => {
      dispatch({
        type: SET_CURRENT_PATIENT,
        payload: { currentPatient: null },
      });
    },
    [dispatch],
  );

  useEffect(() => {
    setSelectedPatientId(id);
    dispatch({
      type: GET_PATIENT_REQUEST,
      payload: { id },
    });
    dispatch({
      type: GET_PATIENT_HISTORY_REQUEST,
      payload: { patientPublicId: id },
    });
  }, [dispatch, id]);

  useEffect(() => {
    if (currentPatient) {
      const treatmentPlan = currentPatient.treatmentPlan;
      if (!treatmentPlan) {
        setApplyTreatPlan(true);
      } else {
        setApplyTreatPlan(false);
      }
    }
  }, [currentPatient, dispatch]);

  useEffect(() => {
    if (currentPatient && shouldShowAlarms) {
      onDrawerClose();
    }
    // eslint-disable-next-line
  }, [currentPatient, shouldShowAlarms]);

  // eslint-disable-next-line no-restricted-globals
  const goToPatients = () => navigate('/patients');

  const editPatient = () => navigate(`/patients/edit/${id}`);

  const selectPatientVersion = (patientPublicId: string) => {
    setSelectedPatientId(patientPublicId);
    setIsDeleteOpen(false);
    setAssignExercisesModalOpened(false);
    dispatch({
      type: GET_PATIENT_REQUEST,
      payload: { id: patientPublicId },
    });
  };

  const applyTreatmentPlan = useCallback(() => {
    if (isReadOnly) return;

    const data = {
      startDate: format(startDate, 'yyyy-MM-dd'),
      templateId: selectedPlan,
      patientId: id,
    };

    dispatch({
      type: APPLY_TREATMENT_PLAN_REQUEST,
      payload: { data },
    });
    setApplyTreatPlan(false);
  }, [dispatch, id, isReadOnly, selectedPlan, startDate]);

  const onCloseDelete = () => setIsDeleteOpen(false);

  const onClickDelete = () => setIsDeleteOpen(true);

  const onDeletePatient = () => {
    if (currentPatient && !isReadOnly) {
      setIsDeleteOpen(false);
      dispatch({
        type: DELETE_PATIENT_REQUEST,
        payload: { patientId: currentPatient.id, navigate },
      });
    }
  };

  const onDrawerClose = useCallback(() => {
    if (isOpenAlarms) {
      dispatch({
        type: GET_ALARMS_SUCCESS,
        payload: { alarms: [] },
      });
    }
    toggleDrawer();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpenAlarms]);

  const tabContent = useMemo(() => {
    if (currentTab === Tabs.CLICKING_PROGRESS_OVERVIEW) {
      return (
        <>
          {!applyTreatPlan && currentPatient?.treatmentPlan && (
            <ClickingOverview
              patientId={selectedPatientId}
              bone={operationTypeToBone(currentPatient.treatmentData.operationType)}
              readOnly={isReadOnly}
            />
          )}
          {applyTreatPlan && !isReadOnly && (
            <ApplyTreatmentPlan
              selectedPlan={selectedPlan}
              setSelectedPlan={setSelectedPlan}
              startDate={startDate}
              setStartDate={setStartDate}
              operationType={currentPatient?.operationType}
              nailTypeMm={currentPatient?.nailTypeMm}
              initialGapMm={currentPatient?.initialGapMm}
              applyTreatmentPlan={applyTreatmentPlan}
              isViewMode
            />
          )}
        </>
      );
    }

    if (currentTab === Tabs.EXERCISE_PERFORMANCE) {
      return <ExercisePerformanceChart />;
    }

    if (currentTab === Tabs.MEDICATIONS) {
      return <Medications key={selectedPatientId} patientId={selectedPatientId} readOnly={isReadOnly} />
    }

    if (currentTab === Tabs.STEPS) {
      return <Steps patientId={selectedPatientId} />
    }

    if (currentTab === Tabs.STEP_ASYMMETRY) {
      return <StepsAsymmetry patientId={selectedPatientId} />
    }
  }, [applyTreatPlan, applyTreatmentPlan, currentPatient?.initialGapMm, currentPatient?.nailTypeMm, currentPatient?.operationType, currentPatient?.treatmentData?.operationType, currentPatient?.treatmentPlan, currentTab, isReadOnly, selectedPatientId, selectedPlan, startDate]);

  return (
    <FullPageContainer
      pageClassName={`${classes.viewPatientContainer} ${isReadOnly ? classes.readOnlyView : ''}`}
    >
      <>
        <ViewPatientControls
          deletePatient={onClickDelete}
          editPatient={editPatient}
          externalLink={currentPatient?.externalId}
          navigateBack={goToPatients}
          showAlarms={onDrawerClose}
          patient={currentPatient}
          activeNotificationCount={currentPatient?.activeNotificationCount}
          readOnly={isReadOnly}
        />
        {currentPatient && (
          <PatientHistorySelector
            history={patientHistory}
            currentPatient={{
              patientPublicId: id,
              status: routePatient?.status ?? patientHistory.find((item: any) => item.patientPublicId === id)?.status ?? '',
              surgeryDate: routePatient?.surgeryDate ?? patientHistory.find((item: any) => item.patientPublicId === id)?.surgeryDate ?? null,
              createdAt: routePatient?.createdAt ?? patientHistory.find((item: any) => item.patientPublicId === id)?.createdAt ?? '',
            }}
            selectedPatientId={selectedPatientId}
            loading={isPatientHistoryLoading}
            onChange={selectPatientVersion}
          />
        )}
        {isReadOnly && (
          <Alert className={classes.readOnlyNotice} severity="info" variant="outlined">
            <strong>
              {isHistoricalVersion ? 'Previous patient version' : 'Archived patient'}
              {' — read-only'}
            </strong>
            <div>
              {isHistoricalVersion
                ? 'You are viewing a historical snapshot. Editing and patient actions are unavailable.'
                : 'This patient record is archived. Editing and patient actions are unavailable.'}
            </div>
          </Alert>
        )}
        <ViewPatientInfo
          patient={currentPatient}
          treatmentData={currentPatient?.treatmentPlan}
          readOnly={isReadOnly}
        />

        <MUITabs
          classes={{
            root: classes.tabs,
            flexContainer: classes.flexContainer,
            indicator: classes.indicator,
          }}
          onChange={(_: React.SyntheticEvent, tab: Tabs) => setCurrentTab(tab)}
          value={currentTab}>
          {tabs.map(({ id, label }, index) => (
            <Tab
              classes={{ root: classes.tab }}
              key={id}
              label={label}
              {...a11yProps(index)}
              value={id}
            />
          ))}
        </MUITabs>

        {tabContent}

        {currentTab === Tabs.MEDICATIONS ? (
          !isReadOnly && <AssignMedicationTemplate />
        ) : (
          <AssignExercises onManageGroupPress={openAssignExerciseModal} readOnly={isReadOnly} />
        )}
        {isAssignExercisesModalOpened && !isReadOnly && (
          <AssignExercisesModal
            onClose={closeAssignExerciseModal}
            {...assignExercisesModalProps!}
          />
        )}
        <Modal
          open={isDeleteOpen}
          title={'Delete patient'}
          message={'Are you sure that you want to delete this patient?'}
          handleCancel={onCloseDelete}
          deleteConfirmation
          handleConfirm={onDeletePatient}
        />
        <Drawer
          anchor="right"
          open={isOpenAlarms}
          onClose={onDrawerClose}
          classes={{ paper: classes.alarmsDrawer }}>
          <Alarms onToggleDrawer={onDrawerClose} patientId={selectedPatientId} />
        </Drawer>
      </>
    </FullPageContainer>
  );
};
